/**
 * /policies/loans/article-90-config — Session 3c PR 7.
 *
 * Owning agent: Mutaba'aa. Special-shape: single-page form (NOT a
 * list). The Article 90 1/3-wage cap is statutory-floored at 33.33% ·
 * cannot be raised · CAN be lowered to be more conservative. The
 * cap input is disabled with a tooltip explaining Mutaba'aa's veto.
 */

import { useQuery } from "@tanstack/react-query";
import { Sparkles } from "lucide-react";
import { useTranslation } from "react-i18next";

import { ErrorPanel, Loading } from "../components/Loading";
import { InlineAISuggestion, TraceableValue } from "../components/ui";
import { useLegalEntityGate } from "../lib/legalEntity";
import { session3cApi } from "../lib/session3c-api";
import type { Article90ConfigItem } from "../lib/session3c-api";
import { showAlertTrace } from "./_session3aHelpers";
import {
  PolicyActionsRow,
  Session3cPolicyShell,
  buildOverrideStackTrace,
} from "./_session3cShell";

export function LoanArticle90ConfigPolicyPage() {
  const { t } = useTranslation();
  const { legalEntityId, gate } = useLegalEntityGate();

  const query = useQuery({
    queryKey: ["s3c-article-90-config", legalEntityId],
    queryFn: () => session3cApi.getArticle90Config(legalEntityId ?? ""),
    enabled: !!legalEntityId,
  });

  if (gate) return gate;

  // Type-narrow: the safeGet may return { items: [] } on 404; treat as
  // missing config (renders the canonical default).
  const cfg: Article90ConfigItem | null =
    query.data && "cap_pct" in (query.data as object)
      ? (query.data as Article90ConfigItem)
      : null;

  return (
    <Session3cPolicyShell
      title={t("policies.article_90.title", "Article 90 Cap Configuration")}
      subtitle={t(
        "policies.article_90.subtitle",
        "1/3 wage-deduction cap enforcement. Statutory floor — cannot be raised. CAN be lowered per LE policy.",
      )}
      agent="mutabaa"
      briefContext={{
        total: 1,
        attention: 0,
        critical: 0,
        top_kind: t("policies.article_90.title", "Article 90 Cap"),
        window_label: cfg ? `cap ${cfg.cap_pct.value}%` : "—",
      }}
      actionsRow={
        <PolicyActionsRow
          domain="loan_article_90"
          newLabel={t("policies.article_90.cta.edit", "Edit Configuration")}
          onNewClick={() =>
            showAlertTrace(
              t(
                "policies.article_90.cta.note",
                "Edit drawer: cap_pct (lower-than-floor only) · aggregation toggles · grace days · warning threshold. The drawer ships in the M3X.LOANS pack.",
              ),
            )
          }
        />
      }
    >
      <InlineAISuggestion
        agent="mutabaa"
        icon={<Sparkles className="h-4 w-4" />}
        severity="info"
        headline={t(
          "policies.article_90.suggestion.headline",
          "Mutaba'aa is the Article 90 floor enforcer.",
        )}
        body={t(
          "policies.article_90.suggestion.body",
          "Cap CANNOT exceed 33.33% (Article 90) — mode-invariantly blocked. Lowering is allowed for more conservative LE policy. Aggregation toggles control which deductions count against the cap.",
        )}
        primaryAction={{
          label: t("policies.article_90.suggestion.open_register", "Open Article 90 Watch"),
          onClick: () => window.location.assign("/operations/loans/article-90-watch"),
        }}
        suggestionKey="article-90-config"
      />

      {query.isLoading && <Loading />}
      {query.isError && <ErrorPanel message={(query.error as Error).message} />}

      {cfg && (
        <div className="overflow-hidden rounded-2xl border border-border-subtle bg-white">
          <table className="w-full text-sm">
            <thead className="bg-surface-sunken">
              <tr className="text-left text-[10px] font-semibold uppercase tracking-wide text-text-secondary">
                <th className="px-3 py-2">{t("policies.article_90.col.parameter", "Parameter")}</th>
                <th className="px-3 py-2">{t("policies.article_90.col.value", "Active value")}</th>
                <th className="px-3 py-2">{t("policies.article_90.col.source", "Source")}</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-t border-border-subtle">
                <td className="px-3 py-2 font-medium">
                  {t("policies.article_90.row.cap", "Article 90 cap (% of monthly wage)")}
                </td>
                <td className="px-3 py-2">
                  <TraceableValue
                    onTraceClick={() =>
                      showAlertTrace(
                        buildOverrideStackTrace(
                          t("policies.article_90.row.cap", "Article 90 cap"),
                          cfg.cap_pct,
                        ),
                      )
                    }
                  >
                    <span className="font-mono font-semibold text-purple-800">
                      {cfg.cap_pct.value}%
                    </span>
                  </TraceableValue>
                </td>
                <td className="px-3 py-2 text-[10px] uppercase text-text-tertiary">
                  {cfg.cap_pct.ksa_article ?? "—"}
                </td>
              </tr>
              <tr className="border-t border-border-subtle">
                <td className="px-3 py-2">{t("policies.article_90.row.agg_loans", "Aggregate loan installments")}</td>
                <td className="px-3 py-2 text-xs">{cfg.aggregation_loans ? "yes" : "no"}</td>
                <td className="px-3 py-2 text-[10px] uppercase text-text-tertiary">le</td>
              </tr>
              <tr className="border-t border-border-subtle">
                <td className="px-3 py-2">{t("policies.article_90.row.agg_garnish", "Aggregate garnishments")}</td>
                <td className="px-3 py-2 text-xs">{cfg.aggregation_garnishments ? "yes" : "no"}</td>
                <td className="px-3 py-2 text-[10px] uppercase text-text-tertiary">le</td>
              </tr>
              <tr className="border-t border-border-subtle">
                <td className="px-3 py-2">{t("policies.article_90.row.agg_advances", "Aggregate travel advances")}</td>
                <td className="px-3 py-2 text-xs">{cfg.aggregation_advances ? "yes" : "no"}</td>
                <td className="px-3 py-2 text-[10px] uppercase text-text-tertiary">le</td>
              </tr>
              <tr className="border-t border-border-subtle">
                <td className="px-3 py-2">{t("policies.article_90.row.grace", "Grace days before veto")}</td>
                <td className="px-3 py-2 font-mono text-xs">{cfg.grace_days}</td>
                <td className="px-3 py-2 text-[10px] uppercase text-text-tertiary">le</td>
              </tr>
              <tr className="border-t border-border-subtle">
                <td className="px-3 py-2">{t("policies.article_90.row.warning", "Warning threshold % (headroom alert)")}</td>
                <td className="px-3 py-2 font-mono text-xs">{cfg.warning_threshold_pct.toFixed(1)}%</td>
                <td className="px-3 py-2 text-[10px] uppercase text-text-tertiary">le</td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </Session3cPolicyShell>
  );
}
